import { useState, useMemo, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useAsset } from "../../context/AssetContext";
import { updateAssetId } from "../../api/updateAssetId";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../context/SessionContext";

const AssetIdEditModal: React.FC = () => {
  const { asset, clientId, isEditAssetIdModalOpen, closeEditAssetIdModal } = useAsset();
  const { user } = useSession(); 
  const navigate = useNavigate();

  const oldAssetId = useMemo(() => {
    if (!asset) return "";
    return asset.assetIdentifier || asset.assetId.split("-")[0];
  }, [asset]);

  const [newAssetId, setNewAssetId] = useState(oldAssetId);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when the modal opens or asset changes
  useEffect(() => {
    if (isEditAssetIdModalOpen) {
      setNewAssetId(oldAssetId);
      setReason("");
      setError(null);
    }
  }, [isEditAssetIdModalOpen, oldAssetId]);


  const isSubmitDisabled = useMemo(() => {
    return newAssetId.trim() === oldAssetId.trim() || !reason.trim() || isSubmitting;
  }, [newAssetId, reason, oldAssetId, isSubmitting]);

  const handleSubmit = async () => {
    if (isSubmitDisabled || !asset) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        pk: asset.pk,
        oldAssetId: oldAssetId.trim(),
        newAssetId: newAssetId.trim(),
        user,
        reason: reason.trim(),
        updateTime: new Date().toISOString(),
      };

      // Call the API function using axios
      await updateAssetId(payload);
      const timestamp = asset.pk.split('-').pop();
      const newFullAssetId = `${newAssetId.trim()}-${timestamp}`;

      // Navigate to the new asset page
      console.log("should be navigating to ", `vehicle/${clientId}/${newFullAssetId}`)
      navigate(`/vehicle/${clientId}/${newFullAssetId}`);
      closeEditAssetIdModal();
    } catch (err) {
      console.error("Error updating asset ID:", err);
      setError("Failed to update Asset ID. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssetIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewAssetId(e.target.value.toUpperCase());
  };

  if (!asset) return null;

  return (
    <Dialog open={isEditAssetIdModalOpen} onClose={closeEditAssetIdModal} fullWidth maxWidth="sm">
      <DialogTitle>Edit Asset ID</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Current Asset ID: {oldAssetId}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="New Asset ID"
            type="text"
            fullWidth
            value={newAssetId}
            onChange={handleAssetIdChange}
            inputProps={{
              style: { textTransform: "uppercase" },
            }}
          />
          <TextField
            margin="dense"
            label="Reason for change"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />
          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeEditAssetIdModal} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSubmitDisabled}>
          {isSubmitting ? <CircularProgress size={24} /> : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetIdEditModal;